import React, { useRef, useState, useEffect } from 'react';
import userManager from '@/API/userManager';
import { formatDate } from '@/common/functions/dateFormater';
import { getUserById } from '../../helpers/getUserById';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import {
	ChatWrapper,
	TranslationWrapper,
	TranslationIcon,
	NoChatData,
	ChatList,
	MessageBox,
	MessageBoxMessage,
	MessageBoxMessageSender,
	MessageBoxOriginal,
	MessageBoxTime,
	FileBox,
	FileTitle,
	StatusChangeWrapper,
	StatusChange,
	ExclamationIconWrapper,
	MessageBoxSenderWrapper,
	SeenWrapper,
	StatusChangeTime,
	SendingMessageWrapper,
	MessageSenderInfoWrapper,
} from './Chat.styled';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { getSortedTicketData } from './helpers/getSortedTicketData';
import { getMessagePosition } from './helpers/getMessagePostition';
import { getMessageBackgroundColor } from './helpers/getMessageBackgroundColor';
import { getMessageText } from './helpers/getMessageText';
import { getSeenMessage } from './helpers/getSeenMessage';
import { getFileById } from '@/API/repositories/storedDocument';
import RichTextViewer from '@/common/components/richTextViewer/RichTextViewer';
import { getQuillDelta } from './helpers/getQuillDelta';
import { isMessageSender } from './helpers/isMessageSender';
import { copyLinkToClipboard } from '@/common/functions/copyLinkToClipboard';
import CopyItem from '../copyItem/CopyItem';
import { getQuillDeltaPlain } from './helpers/getQuillDetlaPlain';
import Icon from '@/common/components/Icon';

const Chat = ({ selectedTicket, messageLoading }) => {
	const [showTranslation, setShowTranslation] = useState();
	const [loadingFiles, setLoadingFiles] = useState([]);
	const [isCopied, setIsCopied] = useState(null);

	const bottomRef = useRef();

	const currentUserId = userManager.getUser().id;

	const {
		commonData: { users },
	} = useCommonDataContext();
	const { makeRequest } = useRequestsContext();

	const handleDownloadFile = async (storedDataId) => {
		setLoadingFiles((prev) => [...prev, storedDataId]);

		const response = await makeRequest(getFileById.bind(null, storedDataId));

		if (response.data) {
			const url = window.URL.createObjectURL(
				new Blob([new Uint8Array(response.data.data.data).buffer]),
			);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', response.data.name);
			document.body.appendChild(link);
			link.click();
		}

		setLoadingFiles((prev) => prev.filter((el) => el !== storedDataId));
	};

	const handleCopyMessage = (message) => {
		const messageContent = message.quill_delta
			? getQuillDeltaPlain(message, currentUserId, showTranslation)
			: getMessageText(message, currentUserId, showTranslation);

		copyLinkToClipboard(messageContent);
		setIsCopied(message.created_at);
		setTimeout(() => setIsCopied(null), 200);
	};

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [selectedTicket, messageLoading]);

	return (
		<ChatWrapper>
			{selectedTicket ? (
				<>
					<TranslationWrapper>
						<TranslationIcon
							onClick={() => setShowTranslation((prev) => !prev)}
							name='language'
							size={32}
						/>
					</TranslationWrapper>
					<ChatList>
						{selectedTicket &&
							getSortedTicketData(selectedTicket).map((element) => (
								<>
									{element.type !== 'status_change' ? (
										<MessageBox
											justifyContent={getMessagePosition(
												element,
												currentUserId,
											)}
											key={element.created_at}
										>
											{isMessageSender(element, currentUserId) &&
												!element.id_in_bucket && (
													<CopyItem
														handleCopy={handleCopyMessage}
														message={element}
													/>
												)}
											<MessageBoxMessage
												backgroundColor={getMessageBackgroundColor(
													element,
													currentUserId,
												)}
											>
												<MessageBoxOriginal
													isCopied={isCopied === element.created_at}
												>
													{element.id_in_bucket ? (
														<FileBox
															onClick={() => handleDownloadFile(element._id)}
														>
															<FileTitle>{element.id_in_bucket}</FileTitle>
															<Icon name='file' size={32} />
														</FileBox>
													) : element.original ? (
														getMessageText(
															element,
															currentUserId,
															showTranslation,
														)
													) : (
														<RichTextViewer
															defaultValue={getQuillDelta(
																element,
																currentUserId,
																showTranslation,
															)}
														/>
													)}
												</MessageBoxOriginal>
												<MessageSenderInfoWrapper>
													<MessageBoxSenderWrapper>
														<MessageBoxMessageSender>
															{getUserById(
																element.sender ||
																	element.created_by ||
																	selectedTicket.created_by,
																users,
															)?.username || 'System'}
														</MessageBoxMessageSender>
													</MessageBoxSenderWrapper>
													<MessageBoxTime>
														{formatDate(element.created_at, 'YYYY/MM/DD HH:mm')}
														{loadingFiles.includes(element._id) && (
															<div>
																<Icon name='spinner' size={16} spin />
															</div>
														)}
													</MessageBoxTime>
												</MessageSenderInfoWrapper>
											</MessageBoxMessage>
											{!isMessageSender(element, currentUserId) &&
												!element.id_in_bucket && (
													<CopyItem
														handleCopy={handleCopyMessage}
														message={element}
													/>
												)}
										</MessageBox>
									) : (
										<StatusChangeWrapper>
											<StatusChange>
												<ExclamationIconWrapper>
													<Icon name='exclamation' size={16} />
												</ExclamationIconWrapper>
												{element.original}
											</StatusChange>
											<StatusChangeTime>
												{formatDate(element.created_at, 'YYYY/MM/DD HH:mm')}
											</StatusChangeTime>
										</StatusChangeWrapper>
									)}
									{element.seen?.filter((userId) => userId !== currentUserId)
										.length > 0 && (
										<SeenWrapper>
											{getSeenMessage(element.seen, users, currentUserId)}
										</SeenWrapper>
									)}
								</>
							))}
						{messageLoading === selectedTicket && (
							<SeenWrapper>
								<SendingMessageWrapper>sending messsage</SendingMessageWrapper>
								<span>
									<Icon name='spinner' size={16} spin />
								</span>
							</SeenWrapper>
						)}
					</ChatList>
				</>
			) : (
				<NoChatData>No data</NoChatData>
			)}
			<div ref={bottomRef} />
		</ChatWrapper>
	);
};

export default Chat;
